/* ==========================================================================
   <dh-video-frame>
   --------------------------------------------------------------------------
   Owns playback for a video rendered inside the DH video frame: the sound
   toggle, the play/pause button, and pausing when the section scrolls out of
   view so audio can never play off screen.

   Deliberately does NOT reuse .wt-hero-video__sound-toggle. That class is
   bound once on DOMContentLoaded in global.js, which means it stops working
   after any theme-editor re-render, and its handler looks for the <video> as a
   direct sibling of the button — which is not true inside this frame. Using
   our own class keeps the two from ever binding to the same button, which
   would toggle mute twice per click and read as dead.

   Being a custom element also means editor re-renders re-upgrade on their own.
   ========================================================================== */

if (!customElements.get('dh-video-frame')) {
  customElements.define(
    'dh-video-frame',
    class DhVideoFrame extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;

        this.soundButton = this.querySelector('.dh-video__sound');
        this.playButton = this.querySelector('.dh-video__play');
        this.scrub = this.querySelector('.dh-video__scrub');
        this.scrubbing = false;

        this.shouldAutoplay = this.dataset.autoplay !== 'false';
        this.pauseOffscreen = this.dataset.pauseOffscreen !== 'false';
        this.restartOnUnmute = this.dataset.restartOnUnmute !== 'false';

        this.userPaused = false;
        this.hasUnmuted = false;

        // Autoplay is only permitted while muted. The sound toggle is the
        // user gesture that earns the right to unmute.
        this.video.muted = true;
        this.video.playsInline = true;
        this.video.setAttribute('playsinline', '');

        this.reducedMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

        if (this.reducedMotion) {
          this.shouldAutoplay = false;
          this.video.autoplay = false;
          this.video.removeAttribute('autoplay');
          this.video.pause();
        }

        this.onSoundClick = this.onSoundClick.bind(this);
        this.onPlayClick = this.onPlayClick.bind(this);
        this.syncPlayState = this.syncPlayState.bind(this);
        this.onIntersect = this.onIntersect.bind(this);
        this.onTimeUpdate = this.onTimeUpdate.bind(this);
        this.onScrubInput = this.onScrubInput.bind(this);
        this.onScrubStart = this.onScrubStart.bind(this);
        this.onScrubEnd = this.onScrubEnd.bind(this);

        this.addEventListeners();
        this.syncSoundState();
        this.syncPlayState();
        this.onTimeUpdate();
        this.observe();
      }

      disconnectedCallback() {
        this.removeEventListeners();
        if (this.observer) this.observer.disconnect();
      }

      addEventListeners() {
        if (this.soundButton) {
          this.soundButton.addEventListener('click', this.onSoundClick);
        }
        if (this.playButton) {
          this.playButton.addEventListener('click', this.onPlayClick);
        }
        this.video.addEventListener('play', this.syncPlayState);
        this.video.addEventListener('pause', this.syncPlayState);

        if (this.scrub) {
          this.video.addEventListener('timeupdate', this.onTimeUpdate);
          this.video.addEventListener('loadedmetadata', this.onTimeUpdate);
          this.scrub.addEventListener('input', this.onScrubInput);
          // Covers mouse, touch and pen. Keyboard fires input without these,
          // which is fine: onScrubInput seeks and never latches scrubbing on.
          this.scrub.addEventListener('pointerdown', this.onScrubStart);
          this.scrub.addEventListener('pointerup', this.onScrubEnd);
          this.scrub.addEventListener('pointercancel', this.onScrubEnd);
        }
      }

      removeEventListeners() {
        if (this.soundButton) {
          this.soundButton.removeEventListener('click', this.onSoundClick);
        }
        if (this.playButton) {
          this.playButton.removeEventListener('click', this.onPlayClick);
        }
        this.video.removeEventListener('play', this.syncPlayState);
        this.video.removeEventListener('pause', this.syncPlayState);

        if (this.scrub) {
          this.video.removeEventListener('timeupdate', this.onTimeUpdate);
          this.video.removeEventListener('loadedmetadata', this.onTimeUpdate);
          this.scrub.removeEventListener('input', this.onScrubInput);
          this.scrub.removeEventListener('pointerdown', this.onScrubStart);
          this.scrub.removeEventListener('pointerup', this.onScrubEnd);
          this.scrub.removeEventListener('pointercancel', this.onScrubEnd);
        }
      }

      observe() {
        if (!this.pauseOffscreen || !('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(this.onIntersect, {
          threshold: [0, 0.25],
        });
        this.observer.observe(this);
      }

      onIntersect(entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
            // Only resume what we paused ourselves. Someone who hit pause
            // stays paused.
            if (this.shouldAutoplay && !this.userPaused && this.video.paused) {
              this.play();
            }
          } else if (!this.video.paused) {
            this.video.pause();
            // Not a user pause: this one should resume on scroll back.
          }
        });
      }

      play() {
        const attempt = this.video.play();
        if (!attempt || typeof attempt.catch !== 'function') return;

        attempt.catch(() => {
          // An unmuted resume can be refused when it is not tied to a gesture.
          // Fall back to muted playback rather than leaving a frozen frame.
          if (!this.video.muted) {
            this.video.muted = true;
            this.syncSoundState();
            const retry = this.video.play();
            if (retry && typeof retry.catch === 'function') {
              retry.catch(() => {});
            }
          }
        });
      }

      onSoundClick(event) {
        event.preventDefault();

        const unmuting = this.video.muted;
        this.video.muted = !this.video.muted;

        if (unmuting && this.restartOnUnmute && !this.hasUnmuted) {
          // First unmute starts the story from the top rather than dropping
          // someone into the middle of a 60 second piece.
          this.video.currentTime = 0;
        }
        if (unmuting) {
          this.hasUnmuted = true;
          this.userPaused = false;
          if (this.video.paused) this.play();
        }

        this.syncSoundState();
      }

      onPlayClick(event) {
        event.preventDefault();

        if (this.video.paused || this.video.ended) {
          this.userPaused = false;
          this.play();
        } else {
          this.userPaused = true;
          this.video.pause();
        }
      }

      // Position is carried as a permille of duration rather than seconds, so
      // the control works before metadata lands and needs no rescaling after.
      onTimeUpdate() {
        if (!this.scrub || this.scrubbing) return;

        const duration = this.video.duration;
        if (!duration || !isFinite(duration)) return;

        const permille = Math.round((this.video.currentTime / duration) * 1000);
        this.scrub.value = String(permille);
        this.setProgress(permille / 10);
      }

      onScrubStart() {
        this.scrubbing = true;
      }

      onScrubEnd() {
        this.scrubbing = false;
      }

      onScrubInput() {
        const duration = this.video.duration;
        if (!duration || !isFinite(duration)) return;

        const permille = Number(this.scrub.value);
        this.video.currentTime = (permille / 1000) * duration;
        this.setProgress(permille / 10);
      }

      setProgress(percent) {
        this.style.setProperty('--dh-progress', percent + '%');
      }

      syncSoundState() {
        if (!this.soundButton) return;

        const on = !this.video.muted;
        this.soundButton.dataset.sound = on ? 'on' : 'off';
        this.soundButton.setAttribute('aria-pressed', on ? 'true' : 'false');
      }

      syncPlayState() {
        this.dataset.playing = this.video.paused ? 'false' : 'true';
      }
    }
  );
}
